import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Redirect, Link } from 'react-router-dom';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import ClipLoader from 'react-spinners/ClipLoader';

import { useFormatMessage } from 'hooks';

// import { fetchEntities as fetchUsers } from 'state/actions/users';
import { createEntity as createEntityLog } from 'state/actions/logs';
import ConfirmationModal from 'components/ConfirmationModal';

export default ({
  yupSchema,
  entityInfos,
  entityActions,
  Profile,
  Form,
  options,
}) => {
  const { i18nEntityName, entitiesName, PATH_ENTITIES } = entityInfos;

  const {
    fetchEntities,
    createEntity,
    updateEntity,
    archiveEntity,
    entitiesCleanUp,
  } = entityActions;

  const { id, action } = useParams();
  const isEditing = useMemo(() => !!id && action === 'edit', [id, action]);
  const isProfile = useMemo(() => !!id && action === 'profile', [id, action]);
  const isCreating = useMemo(() => !id && !action, [id, action]);

  const {
    success,
    entityData = {},
    error,
    // users,
    loading,
    archived,
  } = useSelector(
    (state) => ({
      userId: state.auth.userData.id,
      success: state[entitiesName].success,
      entityData: state[entitiesName].data.find(
        (entity) => entity && entity.id === id
      ),
      // users: state[contactEntitiesName].data,
      error: state[entitiesName].error,
      loading: state[entitiesName].loading,
      archived: state[entitiesName].archived,
    }),
    shallowEqual
  );

  const [archiveModal, setArchiveModal] = useState({
    entityId: null,
    isOpen: false,
  });

  useEffect(() => {
    if (archived && !loading) {
      setArchiveModal((prevState) => ({
        entityId: null,
        isOpen: !prevState.isOpen,
      }));
    }
  }, [archived, loading]);

  const onCloseModalHandler = () => {
    setArchiveModal({ entityId: null, isOpen: false });
  };

  const dispatch = useDispatch();

  // // After an update, refetch clean set success = false in store
  useEffect(() => {
    if (success && isProfile) {
      dispatch(entitiesCleanUp());
    }
  }, [dispatch, success, isProfile]);

  useEffect(() => {
    if (isEditing) {
      if (!entityData) {
        dispatch(fetchEntities(id));
      }
    }
  }, [isEditing, id, entityData, dispatch]);

  // useEffect(() => {
  // if (isProfile) {
  // if (!users || (users && users.length === 0)) {
  //   dispatch(fetchUsers());
  // }
  // }
  // }, [isProfile, users, dispatch]);

  const editEntityMessage = useFormatMessage(`${i18nEntityName}.edit`);

  const newEntityMessage = useFormatMessage(`${i18nEntityName}.create`);

  const onSubmitHandler = (value) => {
    const newEntity = {
      ...value,
    };
    if (id) {
      newEntity.id = id;
    }
    if (isEditing) {
      dispatch(updateEntity(newEntity));
    } else {
      dispatch(createEntity(newEntity));
    }
  };

  const onActivityLogSubmitHandler = (value) => {
    const newLog = { ...value, createdAt: new Date().toISOString() };
    dispatch(createEntityLog(newLog));
  };

  let title = newEntityMessage;
  if (isEditing) {
    title = editEntityMessage;
  } else if (isProfile) {
    title = (
      <span>
        <span className="has-text-weight-normal">
          {useFormatMessage(`${i18nEntityName}.titlePrefix`)}
        </span>
        &nbsp;{entityData.name}
      </span>
    );
  }

  const redirect = isEditing && error && <Redirect to={PATH_ENTITIES} />;
  const redirectSuccess = isEditing && success && (
    <Redirect to={`${PATH_ENTITIES}/${entityData.id}/profile`} />
  );
  const redirectArchived = isProfile && archived && (
    <Redirect to={`${PATH_ENTITIES}`} />
  );
  const redirectCreatingSuccess = isCreating && success && (
    <Redirect to={PATH_ENTITIES} />
  );

  const onRemoveButtonClickHandler = (entityId) => {
    setArchiveModal((prevState) => ({
      entityId,
      isOpen: !prevState.isOpen,
    }));
  };

  const onDeleteEntityHandler = () => {
    dispatch(archiveEntity(archiveModal.entityId));
  };

  const archiveMessage = useFormatMessage(`${i18nEntityName}.archive`);
  const confirmMessage = useFormatMessage(`${i18nEntityName}.confirm`);
  const permArchiveMessage = useFormatMessage(`${i18nEntityName}.permArchive`);
  const cancelMessage = useFormatMessage(`${i18nEntityName}.cancel`);

  return (
    <>
      {redirect}
      {redirectSuccess}
      {redirectCreatingSuccess}
      {redirectArchived}
      {archiveModal.isOpen && (
        <ConfirmationModal
          isActive={archiveModal.isOpen}
          isLoading={loading}
          confirmButtonMessage={archiveMessage}
          title={confirmMessage}
          body={permArchiveMessage}
          cancelButtonMessage={cancelMessage}
          onConfirmation={onDeleteEntityHandler}
          onCancel={onCloseModalHandler}
        />
      )}

      <section className="hero is-hero-bar">
        <div className="hero-body">
          <div className="level">
            <div className="level-left">
              <div className="level-item">
                <h1 className="title">{title}</h1>
              </div>
            </div>
            {isProfile && (
              <div className="level-right">
                <div className="level-item">
                  <Link
                    to={`${PATH_ENTITIES}/${entityData.id}/edit`}
                    className="button"
                  >
                    {useFormatMessage(`${i18nEntityName}.edit`)}
                  </Link>
                </div>{' '}
                <div className="level-item">
                  <button
                    type="button"
                    className="button is-small is-warning"
                    onClick={() => onRemoveButtonClickHandler(id)}
                  >
                    {/* <span className="icon is-small">
                      <i className="mdi mdi-trash-can" />
                    </span> */}
                    {useFormatMessage(`${i18nEntityName}.archive`)}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      <section className="section is-main-section">
        {isProfile && (
          <Profile
            entity={entityData}
            entityInfos={entityInfos}
            options={options}
            // contact={contact}
            onActivityLogSubmitHandler={onActivityLogSubmitHandler}
          />
        )}

        {isEditing && !entityData && <ClipLoader />}
        {(isEditing || isCreating) && (
          <Form
            isProfile={isProfile}
            isEditing={isEditing}
            isCreating={isCreating}
            entity={isEditing ? entityData : yupSchema.getDefault()}
            entityInfos={entityInfos}
            options={options}
            onSubmitHandler={onSubmitHandler}
          />
        )}
      </section>
    </>
  );
};
